import { parseNTriple, type RdfTerm } from "../../../shared/rdf/nTriples";
import { iriLocalName } from "../../../shared/references/shortReferences";
/**
 * Triples in plain language for the review (FR-VERSION-7, NFR-1): an N-Triples line becomes
 * "Monk — is a kind of — Person". Terms are named by the end of their IRI, which tells a
 * renamed term from its old name even when both share a label; the label, where the
 * conflict check knows one, goes with it. Common predicates have their own phrase; any
 * other is named like a term. Pure: the view translates the phrase keys.
 */

export type PlainTerm =
  | { readonly kind: "term"; readonly iri: string; readonly name: string; readonly label: string | null }
  | { readonly kind: "axiom"; readonly label: string }
  | { readonly kind: "text"; readonly text: string; readonly language: string | null };

export interface PlainTriple {
  readonly subject: PlainTerm;
  /** An i18n key under `reviews.predicates`, or `null` to show `predicateName`. */
  readonly predicateKey: string | null;
  readonly predicateName: string;
  readonly object: PlainTerm;
}

const RDF = "http://www.w3.org/1999/02/22-rdf-syntax-ns#";
const RDFS = "http://www.w3.org/2000/01/rdf-schema#";
const OWL = "http://www.w3.org/2002/07/owl#";
const SKOS = "http://www.w3.org/2004/02/skos/core#";

const PREDICATE_KEYS: Readonly<Record<string, string>> = {
  [`${RDF}type`]: "type",
  [`${RDFS}subClassOf`]: "subClassOf",
  [`${RDFS}subPropertyOf`]: "subPropertyOf",
  [`${RDFS}label`]: "label",
  [`${SKOS}prefLabel`]: "label",
  [`${RDFS}comment`]: "comment",
  [`${SKOS}definition`]: "comment",
  [`${RDFS}domain`]: "domain",
  [`${RDFS}range`]: "range",
  [`${OWL}disjointWith`]: "disjointWith",
  [`${OWL}equivalentClass`]: "equivalentClass",
  [`${OWL}inverseOf`]: "inverseOf",
  [`${OWL}onProperty`]: "onProperty",
  [`${OWL}someValuesFrom`]: "someValuesFrom",
  [`${OWL}allValuesFrom`]: "allValuesFrom",
};

/** The line's three terms, or `null` if it is not an N-Triples line. */
export function plainTriple(line: string, labels: Readonly<Record<string, string>>): PlainTriple | null {
  const triple = parseNTriple(line);
  if (triple === null) {
    return null;
  }
  return {
    subject: termOf(triple.subject, labels),
    predicateKey: PREDICATE_KEYS[triple.predicate] ?? null,
    predicateName: iriLocalName(triple.predicate),
    object: termOf(triple.object, labels),
  };
}

function termOf(term: RdfTerm, labels: Readonly<Record<string, string>>): PlainTerm {
  switch (term.kind) {
    case "iri":
      return { kind: "term", iri: term.iri, name: iriLocalName(term.iri), label: labels[term.iri] ?? null };
    case "blank":
      return { kind: "axiom", label: term.label };
    case "literal":
      return { kind: "text", text: term.text, language: term.language };
  }
}
